/**
 * Parse various date inputs to a valid `Date` instance.
 * Safari-safe for `YYYY-MM-DD` strings.
 * @param {any} value
 * @returns {Date | null}
 */
export function parseToDate(value) {
    if (value == null) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    if (Array.isArray(value)) {
        if (value.length < 3) return null;

        const [rawYear, rawMonth = 1, rawDay = 1, rawHour = 0, rawMinute = 0, rawSecond = 0, rawMs = 0] = value;
        const year = Number(rawYear);
        const month = Number(rawMonth);
        const day = Number(rawDay);
        const hour = Number(rawHour);
        const minute = Number(rawMinute);
        const second = Number(rawSecond);
        const millisecond = Number(rawMs);

        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
            return null;
        }

        const date = new Date(
            year,
            Math.max(0, month - 1),
            day,
            Number.isFinite(hour) ? hour : 0,
            Number.isFinite(minute) ? minute : 0,
            Number.isFinite(second) ? second : 0,
            Number.isFinite(millisecond) ? millisecond : 0
        );

        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;

        try {
            // Safari doesn't reliably parse YYYY-MM-DD; handle date-only explicitly.
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
            if (m) {
                const [, yyyy, mm, dd] = m;
                const dateOnly = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
                return Number.isNaN(dateOnly.getTime()) ? null : dateOnly;
            }

            const parsed = new Date(trimmed);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }

            const normalized = trimmed.replace(/\s/, "T");
            const withZone =
                /^[\d-]+T[\d:]+$/.test(normalized) && !/[Z+-]\d{2}:?\d{2}$/.test(normalized)
                    ? `${normalized}Z`
                    : normalized;

            const retry = new Date(withZone);
            return Number.isNaN(retry.getTime()) ? null : retry;
        } catch {
            return null;
        }
    }

    if (typeof value === "number") {
        const ms = value < 10_000_000_000 ? value * 1000 : value;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
    }

    return null;
}

// === Nieuw / vervangen ===
export function calculateAge(dateBirth, asOf = new Date()) {
    const dob = parseToDate(dateBirth);
    const ref = parseToDate(asOf);

    if (!dob || !ref) {
        return undefined;
    }

    let age = ref.getFullYear() - dob.getFullYear();
    const beforeBirthday =
        ref.getMonth() < dob.getMonth() ||
        (ref.getMonth() === dob.getMonth() && ref.getDate() < dob.getDate());
    if (beforeBirthday) age--;
    return age;
}

// Volgende verjaardag binnen het schooljaar (of null)
export function nextBirthdayInYear(dateBirth, schoolYear) {
    if (!schoolYear?.startDate || !schoolYear?.endDate) return null;
    const start = parseToDate(schoolYear.startDate);
    const end = parseToDate(schoolYear.endDate);
    const baseDob = parseToDate(dateBirth);

    if (!start || !end || !baseDob) return null;

    let bday = new Date(baseDob);
    bday.setFullYear(start.getFullYear());

    // Als de verjaardag vóór start ligt, pak het jaar erna
    if (bday < start) {
        bday = new Date(baseDob);
        bday.setFullYear(start.getFullYear() + 1);
    }

    return bday >= start && bday <= end ? bday : null;
}

// Compacte label-formatter: "2 → 3 in nov"
export function formatAgeAtYearStart(dateBirth, schoolYear, locale = "nl-NL") {
    const asOf = schoolYear?.startDate ?? new Date();
    const ageAtStart = calculateAge(dateBirth, asOf);
    const bdayInYear = nextBirthdayInYear(dateBirth, schoolYear);
    if (!bdayInYear) return `${ageAtStart}`;

    const newAge = ageAtStart + 1;
    const monthShort = bdayInYear.toLocaleString(locale, { month: "long" }); // "nov"
    return `${newAge} in ${monthShort}`;
}

// 📌 dateUtils.js
export function calculateAgeAtDate(dateOfBirth, referenceDate) {
    const birthDate = parseToDate(dateOfBirth);
    const refDate = parseToDate(referenceDate);

    if (!birthDate || !refDate) return undefined;

    let age = refDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = refDate.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && refDate.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
}

export const toIsoDate = (val) => {
    if (Array.isArray(val)) {
        const [year, month, day] = val;
        // Format as yyyy-mm-dd manually to avoid timezone shift
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    if (typeof val === "string") {
        return val.split("T")[0];
    }
    return "";
};

export const parseDateArray = (arr) => {
    if (!Array.isArray(arr)) return null;
    return parseToDate(arr);
};

export const formatDate = (dateStr) => {
    const date = parseToDate(dateStr);
    if (!date) return "-";
    return date.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};

export const formatDateDMY = (value) => {
    const date = parseToDate(value);
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};
